import { Injectable, effect, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppService, Palette, Theme } from './app.service';
import { PageTourService } from './page-tour.service';
import { KeycloakPasswordAuthService } from './auth/keycloak-password-auth.service';

/** Wire shape of GET/PUT /api/v1/me/preferences (identity-service). */
interface UserPreferencesDto {
  theme?: Theme | null;
  palette?: Palette | null;
  tourEnabled?: boolean | null;
  mascotEnabled?: boolean | null;
  mascotIdle?: number | null;
  mascotAway?: number | null;
  mascotSpeed?: number | null;
}

/**
 * Persists three per-user UI settings to identity-service so they follow the
 * user across logins/devices instead of living only in this browser's
 * localStorage tweaks bag:
 *   • dark mode           (AppService.theme)
 *   • colour palette      (AppService.palette — Settings → Branding)
 *   • guided-tours toggle (PageTourService.toursEnabled — sidebar)
 *
 * On login (a Keycloak profile appears) it GETs the stored prefs and applies
 * them; thereafter any change to the three signals is PUT back (debounced).
 * Falls back to localStorage-only when there's no session (dev-bypass / offline),
 * so nothing breaks without a real login.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly http = inject(HttpClient);
  private readonly app = inject(AppService);
  private readonly pageTour = inject(PageTourService);
  private readonly auth = inject(KeycloakPasswordAuthService, { optional: true });

  private readonly URL = '/api/v1/me/preferences';
  private readonly loaded = signal(false);
  /** JSON of the last value we loaded or saved — so applying loaded prefs
   *  doesn't immediately echo a redundant PUT, and no-op changes don't save. */
  private lastSynced = '';
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Load once, when a signed-in profile becomes available.
    effect(() => {
      const sub = this.auth?.profile()?.sub;
      if (sub && !this.loaded()) this.load();
    });

    // Save when any tracked setting changes — but only after the initial load
    // and while authenticated (so we never persist pre-login defaults).
    effect(() => {
      const prefs = this.snapshot();
      if (!this.loaded() || !this.auth?.profile()?.sub) return;
      const key = JSON.stringify(prefs);
      if (key === this.lastSynced) return;
      this.scheduleSave(prefs, key);
    });
  }

  /** Current value of every persisted setting — the single source of the PUT
   *  body and the lastSynced fingerprint (keep the two in lock-step). */
  private snapshot(): UserPreferencesDto {
    return {
      theme: this.app.theme(),
      palette: this.app.palette(),
      tourEnabled: this.pageTour.toursEnabled(),
      mascotEnabled: this.app.mascotEnabled(),
      mascotIdle: this.app.mascotIdle(),
      mascotAway: this.app.mascotAway(),
      mascotSpeed: this.app.mascotSpeed(),
    };
  }

  private load(): void {
    this.http.get<UserPreferencesDto>(this.URL).subscribe({
      next: (p) => {
        this.apply(p ?? {});
        // Seed lastSynced with the applied values so the save effect no-ops.
        this.lastSynced = JSON.stringify(this.snapshot());
        this.loaded.set(true);
      },
      // No stored prefs / offline → keep whatever's local; still enable saving
      // so the user's next change (and current state) gets persisted.
      error: () => this.loaded.set(true),
    });
  }

  private apply(p: UserPreferencesDto): void {
    if (p.theme === 'light' || p.theme === 'dark') this.app.setTheme(p.theme);
    if (p.palette) this.app.setPalette(p.palette);
    if (typeof p.tourEnabled === 'boolean') this.pageTour.setToursEnabled(p.tourEnabled);
    if (typeof p.mascotEnabled === 'boolean') this.app.setMascotEnabled(p.mascotEnabled);
    if (typeof p.mascotIdle === 'number') this.app.setMascotIdle(p.mascotIdle);
    if (typeof p.mascotAway === 'number') this.app.setMascotAway(p.mascotAway);
    if (typeof p.mascotSpeed === 'number') this.app.setMascotSpeed(p.mascotSpeed);
  }

  private scheduleSave(prefs: UserPreferencesDto, key: string): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.http.put(this.URL, prefs).subscribe({
        next: () => (this.lastSynced = key),
        error: () => {}, // best-effort; localStorage already holds the value
      });
    }, 400);
  }
}
